import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { deepMap } from 'react-children-utilities'
import { usePopper } from 'react-popper'
import IconChevron from '@/assets/svgs/profile-chevron.svg?react'

const Item = () => null

Item.propTypes = {
  id: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node,
}

const dropdownItemClassNames = (value, firstChild) => [
  'py-2.5 px-3 text-sm hover:bg-grey-90',
  firstChild.props.disabled ? 'cursor-not-allowed opacity-60' : '',
]

const Dropdown = ({
  children,
  placeholder,
  value,
  placement = 'bottom-start',
  disabled,
  ...rest
}) => {
  const [visible, setVisibility] = useState(false)

  const referenceRef = useRef(null)
  const popperRef = useRef(null)

  const { styles, attributes, update } = usePopper(referenceRef.current, popperRef.current, {
    placement,
    modifiers: [{ name: 'offset', enabled: true, options: { offset: [0, 5] } }],
  })

  useEffect(() => {
    if (visible && typeof update === 'function') {
      requestAnimationFrame(() => update())
    }
  }, [visible, update])

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick)
    return () => document.removeEventListener('mousedown', handleDocumentClick)
  }, [])

  function handleDocumentClick(event) {
    if (referenceRef.current?.contains(event.target) || popperRef.current?.contains(event.target))
      return
    setVisibility(false)
  }

  function handleDropdownClick() {
    setVisibility(v => !v)
  }

  const items = deepMap(children, firstChild => {
    if (firstChild.type === Item) {
      return deepMap(firstChild.props.children, (child, childIndex) => (
        <div
          className={clsx(
            'cursor-pointer whitespace-nowrap',
            dropdownItemClassNames(value, firstChild)
          )}
          onClick={event => {
            handleDropdownClick()
            typeof firstChild.props.onClick === 'function' && firstChild.props.onClick(event)
          }}
          key={childIndex}
        >
          {child}
        </div>
      ))
    }
    return firstChild
  })

  return (
    <div className="text-sm bg-white relative" {...rest}>
      <div
        className={clsx(
          'min-h-[2.8rem] py-2.25 pl-3.5 pr-8 border border-grey-90 rounded-[5px] bg-white flex items-center justify-between overflow-hidden cursor-pointer',
          disabled && 'cursor-not-allowed text-grey-50 bg-grey-98 [&_svg]:fill-grey-50'
        )}
        ref={referenceRef}
        onClick={handleDropdownClick}
        data-testid="dropdown-select"
      >
        {value ? (
          <span className="overflow-hidden whitespace-nowrap text-ellipsis">{value}</span>
        ) : (
          <span
            className={clsx(
              'font-avenir text-sm font-medium text-grey-50 overflow-hidden w-full h-5',
              disabled && 'text-grey-80'
            )}
          >
            {placeholder}
          </span>
        )}
        <div>
          <IconChevron />
        </div>
      </div>
      {!disabled && (
        <div ref={popperRef} style={styles.popper} {...attributes.popper}>
          <div
            className={clsx(
              'py-2.5 border border-grey-90 rounded-[5px] bg-white max-h-[350px] overflow-y-scroll transition-opacity duration-150 ease-in-out',
              visible ? 'flex flex-col opacity-100' : 'opacity-0 invisible absolute -left-[9999px]'
            )}
            data-testid="dropdown-content"
          >
            {items}
          </div>
        </div>
      )}
    </div>
  )
}

Dropdown.propTypes = {
  children: PropTypes.node.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  placement: PropTypes.string,
  disabled: PropTypes.bool,
}

Dropdown.Item = Item

export default Dropdown
